import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/contexts/auth';
import { useToast } from '@/hooks/use-toast';
import { useAppErrorToast } from '@/hooks/use-app-error-toast';
import { useSignupInvitationToken } from '@/hooks/use-signup-invitation-token';
import { authService } from '@/services/auth';
import { applyFormErrorDetails } from '@/lib/form-errors';

import { partnerSignupSchema, PartnerSignupFormData } from '../utils/schema';

export function usePartnerSignup() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const { toast } = useToast();
  const { showErrorToast } = useAppErrorToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [partnerViewerComplete, setPartnerViewerComplete] = useState(false);
  const invitation = useSignupInvitationToken();

  const emailFromUrl = invitation.emailFromUrl;
  const tokenFromUrl = invitation.token;

  const form = useForm<PartnerSignupFormData>({
    resolver: zodResolver(partnerSignupSchema),
    mode: 'onChange',
  });

  const password = form.watch('password');

  useEffect(() => {
    if (emailFromUrl) {
      form.setValue('email', emailFromUrl);
    }
  }, [emailFromUrl, form]);

  const onSubmit = async (data: PartnerSignupFormData) => {
    try {
      setIsSubmitting(true);

      const response = await authService.signup({
        email: data.email,
        password: data.password,
        confirmationPassword: data.confirmPassword,
        token: tokenFromUrl || undefined,
      });

      if (response.role === 'PARTNER_VIEWER') {
        setPartnerViewerComplete(true);
        return;
      }

      try {
        await refreshUser();
      } catch {
      }

      toast({
        title: 'Conta ativada com sucesso!',
        description: 'Bem-vindo ao Culture Monitor!',
        variant: 'success',
      });

      navigate('/app/reports');
    } catch (error: unknown) {
      const normalizedError = showErrorToast(error, {
        title: 'Erro ao ativar conta',
        fallbackMessage: 'Não foi possível ativar sua conta. Por favor, tente novamente.',
      });
      applyFormErrorDetails(normalizedError.details, form.setError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    form,
    password,
    isSubmitting,
    emailFromUrl,
    partnerViewerComplete,
    onSubmit,
    invitation,
  };
}
